import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  locationService,
  State as LocationState,
  District as LocationDistrict,
} from '../../services/locationService';
import type { RootState } from '../index';

/**
 * locationScope slice
 *
 * Holds the state + district lists the logged-in user can pick from.
 *
 * Two entry points, same storage:
 *   - Non-admin login → `loadAssignedLocations({stateIds, districtIds})` resolves
 *     the user's assigned IDs via POST /Districts/locations-by-ids.
 *   - Admin login → `loadFullCatalog()` pulls every state + district from
 *     /Districts/states and /Districts/school-districts.
 *
 * Either way, components consume the same `states` and `districts` keys via
 * `selectAssignedStates` / `selectAssignedDistricts`.
 */

export type LocationScopeStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

interface LoadAssignedLocationsArgs {
  stateIds: string[];
  districtIds: string[];
  /**
   * Dispatch Area IDs. Tri-state per Copilot review on COMP-1682:
   *   - `null` or omitted → unrestricted (admin / full-catalog callers).
   *   - `[]` → user has zero authorized DAs; nothing allowed.
   *   - `[id, ...]` → specific subset.
   * Downstream filtering must distinguish "no filter" from "filter to
   * nothing" — an empty array on the driver path used to conflate the two.
   */
  dispatchAreaIds?: string[] | null;
}

interface LocationScopeState {
  states: LocationState[];
  districts: LocationDistrict[];
  /** See `LoadAssignedLocationsArgs.dispatchAreaIds` for tri-state semantics. */
  dispatchAreaIds: string[] | null;
  status: LocationScopeStatus;
  error: string | null;
}

const initialState: LocationScopeState = {
  states: [],
  districts: [],
  dispatchAreaIds: null,
  status: 'idle',
  error: null,
};

export const loadAssignedLocations = createAsyncThunk<
  { states: LocationState[]; districts: LocationDistrict[]; dispatchAreaIds: string[] | null },
  LoadAssignedLocationsArgs,
  { rejectValue: string }
>('locationScope/loadAssignedLocations', async ({ stateIds, districtIds, dispatchAreaIds }, thunkApi) => {
  try {
    const { states, districts } = await locationService.fetchLocationsByIds(stateIds, districtIds);
    // Callers that don't pass dispatchAreaIds inherit `null` (unrestricted),
    // matching the admin-path default. Callers that pass an array (empty or
    // not) get the exact subset stored — including the "zero DAs" case.
    return { states, districts, dispatchAreaIds: dispatchAreaIds ?? null };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to load assigned locations';
    return thunkApi.rejectWithValue(message);
  }
});

/**
 * Admin path — pulls the full state + district catalog in parallel and stores
 * it in the same slice keys as `loadAssignedLocations`, so every consumer
 * (Fleet / Rental / Person dialogs) can read from the same selectors.
 */
export const loadFullCatalog = createAsyncThunk<
  { states: LocationState[]; districts: LocationDistrict[]; dispatchAreaIds: string[] | null },
  void,
  { rejectValue: string }
>('locationScope/loadFullCatalog', async (_, thunkApi) => {
  try {
    const [states, districts] = await Promise.all([
      locationService.fetchStates(false),
      locationService.fetchAllDistricts(false),
    ]);
    // Admin path is unrestricted; `null` signals "no subset filter" to
    // downstream consumers (distinct from `[]` which means the caller
    // explicitly said no DAs are allowed).
    return { states, districts, dispatchAreaIds: null };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to load full location catalog';
    return thunkApi.rejectWithValue(message);
  }
});

const locationScopeSlice = createSlice({
  name: 'locationScope',
  initialState,
  reducers: {
    clearLocationScope: (state) => {
      state.states = [];
      state.districts = [];
      state.dispatchAreaIds = null;
      state.status = 'idle';
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    const setLoading = (state: LocationScopeState) => {
      state.status = 'loading';
      state.error = null;
    };
    const setSucceeded = (
      state: LocationScopeState,
      action: PayloadAction<{
        states: LocationState[];
        districts: LocationDistrict[];
        dispatchAreaIds: string[] | null;
      }>
    ) => {
      state.status = 'succeeded';
      state.states = action.payload.states;
      state.districts = action.payload.districts;
      state.dispatchAreaIds = action.payload.dispatchAreaIds;
      state.error = null;
    };
    const setFailed = (fallback: string) =>
      (
        state: LocationScopeState,
        action: { payload?: string | undefined; error: { message?: string | undefined } }
      ) => {
        state.status = 'failed';
        state.error = action.payload ?? action.error.message ?? fallback;
      };

    builder
      .addCase(loadAssignedLocations.pending, setLoading)
      .addCase(loadAssignedLocations.fulfilled, setSucceeded)
      .addCase(loadAssignedLocations.rejected, setFailed('Failed to load assigned locations'))
      .addCase(loadFullCatalog.pending, setLoading)
      .addCase(loadFullCatalog.fulfilled, setSucceeded)
      .addCase(loadFullCatalog.rejected, setFailed('Failed to load full location catalog'));
  },
});

export const { clearLocationScope } = locationScopeSlice.actions;

export const selectAssignedStates = (state: RootState): LocationState[] =>
  state.locationScope.states;

export const selectAssignedDistricts = (state: RootState): LocationDistrict[] =>
  state.locationScope.districts;

/**
 * Driver's assigned Dispatch Area IDs. Tri-state (see LoadAssignedLocationsArgs):
 *   - `null` for admin / full-catalog (unrestricted).
 *   - `[]` for a user with zero authorized DAs (nothing allowed).
 *   - `[id, ...]` for a specific subset.
 * Downstream must distinguish `null` from `[]` — the old "empty = unfiltered"
 * shortcut broke the zero-DA case.
 */
export const selectAssignedDispatchAreaIds = (state: RootState): string[] | null =>
  state.locationScope.dispatchAreaIds;

export const selectLocationScopeStatus = (state: RootState): LocationScopeStatus =>
  state.locationScope.status;

export const selectLocationScopeError = (state: RootState): string | null =>
  state.locationScope.error;

export default locationScopeSlice.reducer;
